import {
    Component,
    Host,
    Prop,
    h,
    ComponentInterface,
    EventEmitter,
    Event,
    Element,
    Method,
    Watch,
    State,
} from "@stencil/core";
import { ButtonVariant } from "../button/button";
import { lockBodyScrolling, unlockBodyScrolling } from "../../utils/scroll";

export type DialogCloseButtonType =
    | ButtonVariant
    | "close-button"
    | "background";

export type DialogCloseEvent = {
    trigger: DialogCloseButtonType | string;
};

export type DialogType = "default" | "warning" | "error";
export type DialogSize = "small" | "medium-height" | "medium" | "large";

/**
 * @slot (default) - dialog content
 * @slot icon - custom icon, you have to align it yourself, alternatively use the icon attribute
 * @slot trailing-icon - custom icon to the right of the title, alternatively use the trailing-icon attribute
 * @slot footer-button - footer buttons to add, use <uic-button>. They may be wrapped by e.g. <uic-tooltip>
 * @slot footer-button-secondary - separate set of buttons to add in dialog's footer left aligned, use <uic-button>. They may be wrapped by e.g. <uic-tooltip>
 */
@Component({
    tag: "uic-dialog",
    styleUrl: "dialog.scss",
})
export class Dialog implements ComponentInterface {
    private isShowing = false;
    private typeIconMap: { [type in DialogType]: string } = {
        error: "uic:flag-error",
        warning: "uic:flag-warning",
        default: null,
    };
    private panel: HTMLElement;
    private footer: HTMLElement;
    private observer: MutationObserver;

    @Element() host: HTMLUicDialogElement;

    @State() hasHeader = true;
    @State() hasFooter = true;

    /** Title of the dialog, displayed in the header */
    @Prop() dialogTitle: string;

    /**
     * Controls whether dialog can be dismissed without picking any option
     * provided as a footer button.
     *
     * - If true, an X mark is rendered in top-right corner of dialog and clicking
     * it dismisses the dialog without confirming any action offered as a button
     * in dialog's footer.
     *
     * - If false, there is X mark available for clicking. In addition, setting it
     * `false` is required to hide the header.
     */
    @Prop() dismissable = true;

    /**
     * Controls whether clicking background of dialog is dismissing it.
     *
     * - If true, clicking background of dialog is dismissing it without
     *   confirming any action offered as a button in dialog's footer.
     *
     * - If false, clicking background is ignored.
     *
     * By default, state of `dismissable` property is adopted.
     */
    @Prop() backgroundDismissable: boolean;

    /** Predefined dialog type for common use cases */
    @Prop() type: DialogType = "default";

    /** Custom icon to show to the left of the title */
    @Prop() icon?: string;

    /** Custom icon to show to the right of the title */
    @Prop() trailingIcon?: string;

    /** Dialog sizes as defined in the styleguide */
    @Prop() size: DialogSize = "small";

    /** Whether to render the background/backdrop, clicks will work anyway */
    @Prop() hasBackdrop: boolean = true;

    /**
     * There is a fixed top margin because the main menu (which most styleguide apps should have),
     * should not be blurred.
     * Use this prop to make the background/backdrop fill the full screen instead.
     */
    @Prop() isOnPageWithoutMainMenu: boolean = false;

    /** May be used as alternative to calling the open() and close() methods */
    @Prop({ mutable: true, reflect: true }) isOpen = this.isShowing;

    /**
     * Triggered when the dialog is about to close.
     * Use `preventDefault` within the handler to cancel closing.
     */
    @Event() uicClose: EventEmitter<DialogCloseEvent>;

    /**
     * Triggered when the dialog is about to open.
     * Use `preventDefault` within the handler to cancel opening.
     */
    @Event() uicOpen: EventEmitter;

    @Watch("isOpen")
    handleOpenChange() {
        this.isOpen ? this.open() : this.close();
    }

    @Watch("dialogTitle")
    @Watch("dismissable")
    @Watch("type")
    @Watch("icon")
    updateHeaderState() {
        this.hasHeader =
            !!this.dialogTitle ||
            this.dismissable ||
            this.type !== "default" ||
            !!this.icon;
    }

    componentWillLoad() {
        this.updateHeaderState();
        this.hasFooter = this.getFooterSlotCount() > 0;
        this.handleOpenChange();
    }

    componentDidLoad() {
        this.observer = new MutationObserver(() => {
            const footerSlotElementCount = this.getFooterSlotCount();
            if (footerSlotElementCount > 0) {
                this.hasFooter = true;
            } else {
                this.hasFooter = false;
            }
        });

        this.observer.observe(this.footer, {
            attributes: false,
            childList: true,
            characterData: false,
        });
    }

    disconnectedCallback() {
        unlockBodyScrolling(this.host);
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }

    /** close the dialog */
    @Method()
    public async close(trigger?: DialogCloseButtonType | string) {
        if (!this.isShowing) {
            return;
        }

        const uicCloseEvent = this.uicClose.emit({ trigger: trigger });
        if (uicCloseEvent.defaultPrevented) {
            this.isOpen = true;
            return;
        }

        this.isShowing = false;
        this.isOpen = false;

        unlockBodyScrolling(this.host);
        document.removeEventListener("focusin", (e) =>
            this.onDocumentFocusIn(e)
        );
    }

    /** open the dialog */
    @Method()
    public async open() {
        if (this.isShowing) {
            return;
        }

        const uicOpenEvent = this.uicOpen.emit();
        if (uicOpenEvent.defaultPrevented) {
            this.isOpen = false;
            return;
        }

        this.isShowing = true;
        this.isOpen = true;

        lockBodyScrolling(this.host);
        document.addEventListener("focusin", (e) => this.onDocumentFocusIn(e));

        // we have to wait for the trigger button click event to complete
        // else the focus call below will be overwritten by the click event
        setTimeout(() => this.panel.focus(), 20);
    }

    private getFooterSlotCount() {
        // we can't use `this.footer`,
        // because it is not available in componentWillLoad
        // using componentDidLoad would trigger another render
        return this.host.querySelectorAll<HTMLElement>(
            "[slot='footer-button'], [slot='footer-button-secondary']"
        ).length;
    }

    private onBackgroundClick(e) {
        e.stopPropagation();
        if (
            this.backgroundDismissable ||
            (this.backgroundDismissable == null && this.dismissable)
        ) {
            this.close("background");
        }
    }

    private onDocumentFocusIn(event: Event) {
        const target = event.target as HTMLElement;

        if (target.closest("uic-dialog") === null) {
            this.panel.focus();
        }
    }

    private onFooterClick(event: Event) {
        const uicButton =
            event.target instanceof HTMLButtonElement
                ? (event.target.parentElement as HTMLUicButtonElement)
                : (event.target as HTMLUicButtonElement);

        if (uicButton.hasAttribute("uic-close-dialog-button")) {
            this.close(uicButton.variant);
        }
    }

    private renderHeader() {
        return (
            <div
                class={{
                    "uic-dialog__panel-header": true,
                    "uic-hidden": !this.hasHeader,
                }}
            >
                <div class="uic-dialog__panel-header-inner">
                    <slot name="icon">
                        {this.icon || this.type !== "default" ? (
                            <div class="uic-dialog__panel-header-icon">
                                <uic-icon
                                    name={
                                        this.icon || this.typeIconMap[this.type]
                                    }
                                ></uic-icon>
                            </div>
                        ) : (
                            <span></span>
                        )}
                    </slot>

                    <div class="uic-dialog__panel-header-title">
                        {this.dialogTitle}
                    </div>

                    <slot name="trailing-icon">
                        {this.trailingIcon ? (
                            <uic-icon
                                class="uic-dialog__panel-header-trailing-icon"
                                name={this.trailingIcon}
                                size="small"
                            ></uic-icon>
                        ) : (
                            <span></span>
                        )}
                    </slot>

                    {this.dismissable && (
                        <uic-icon-button
                            class="uic-dialog__panel-header-close-button"
                            onClick={() => {
                                this.close("close-button");
                            }}
                            icon="uic:cross"
                        ></uic-icon-button>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const backdrop = this.hasBackdrop ? (
            <div class="uic-dialog__backdrop"></div>
        ) : undefined;

        return (
            <Host
                class={{
                    "uic-dialog": true,
                    "uic-dialog--without-main-menu-margin": this
                        .isOnPageWithoutMainMenu,
                }}
                style={{ display: this.isShowing ? "block" : "none" }}
            >
                {backdrop}
                <div
                    class={{
                        "uic-dialog__background": true,
                    }}
                    onClick={(e) => this.onBackgroundClick(e)}
                >
                    <div
                        ref={(el) => (this.panel = el)}
                        class={{
                            "uic-dialog__panel": true,
                            "uic-dialog__trumpf-ui": true,
                            [`uic-dialog__panel--size-${this.size}`]:
                                this.size !== "small",
                        }}
                        onClick={(e) => e.stopPropagation()}
                        tabindex="-1"
                    >
                        {this.renderHeader()}

                        <uic-scrollpanel class="uic-dialog__panel-scroll">
                            <slot></slot>
                        </uic-scrollpanel>

                        <uic-footer
                            class={{
                                "uic-dialog__footer": true,
                                "uic-hidden": !this.hasFooter,
                            }}
                            ref={(el) => (this.footer = el)}
                            onClick={(e) => this.onFooterClick(e)}
                        >
                            <span slot="secondary">
                                <slot name="footer-button-secondary"></slot>
                            </span>
                            <slot name="footer-button"></slot>
                        </uic-footer>
                    </div>
                </div>
            </Host>
        );
    }
}
